import { api } from './client';
import type { User } from '../types';

export const authApi = {
  register: (data: { email: string; username: string; password: string; displayName?: string; nationalityCode?: string; acceptedTerms: true; locale?: string; invitationToken?: string }) =>
    api.post<{ accessToken: string; user: User; acceptedTripId: string | null }>('/auth/register', data).then(r => r.data),

  completeProfile: (data: { username: string; nationalityCode?: string; acceptedTerms: true }) =>
    api.post<void>('/auth/complete-profile', data).then(r => r.data),

  login: (data: { email: string; password: string }) =>
    api.post<{ accessToken: string; user: User; requiresTwoFactor?: boolean; twoFactorToken?: string }>('/auth/login', data).then(r => r.data),

  verifyTwoFactor: (data: { twoFactorToken: string; code: string }) =>
    api.post<{ accessToken: string; user: User }>('/auth/verify-2fa', data).then(r => r.data),

  refresh: () =>
    api.post<{ accessToken: string; user: User }>('/auth/refresh').then(r => r.data),

  logout: () => api.post('/auth/logout'),

  me: () => api.get<User>('/auth/me').then(r => r.data),

  updateMe: (data: { displayName?: string; nationalityCode?: string; birthDate?: string | null }) =>
    api.patch<User>('/auth/me', data).then(r => r.data),

  uploadAvatar: (image: Blob, filename: string) => {
    const form = new FormData();
    form.append('file', image, filename);
    return api.post<User>('/users/me/avatar', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then(r => r.data);
  },

  removeAvatar: () => api.delete<User>('/users/me/avatar').then(r => r.data),

  setup2FA: () =>
    api.post<{ secret: string; qrCode: string }>('/auth/2fa/setup').then(r => r.data),

  enable2FA: (data: { secret: string; code: string }) =>
    api.post<{ twoFactorEnabled: boolean }>('/auth/2fa/enable', data).then(r => r.data),

  disable2FA: (data: { code: string }) =>
    api.post<{ twoFactorEnabled: boolean }>('/auth/2fa/disable', data).then(r => r.data),

  deleteAccount: () => api.delete('/auth/account'),

  exportMyData: () =>
    api.get('/auth/me/export', { responseType: 'blob' }).then(r => r.data as Blob),

  verifyEmail: (token: string) =>
    api.post<void>('/auth/verify-email', { token }).then(r => r.data),

  resendVerification: (locale?: string) =>
    api.post<void>('/auth/resend-verification', { locale }).then(r => r.data),

  changePassword: (data: { currentPassword: string; newPassword: string }) =>
    api.post<{ accessToken: string; user: User }>('/auth/change-password', data).then(r => r.data),

  forgotPassword: (data: { email: string; locale?: string }) =>
    api.post<void>('/auth/forgot-password', data).then(r => r.data),

  checkResetToken: (token: string) =>
    api.get<{ twoFactorRequired: boolean }>('/auth/reset-password', { params: { token } }).then(r => r.data),

  resetPassword: (data: { token: string; password: string; twoFactorCode?: string }) =>
    api.post<void>('/auth/reset-password', data).then(r => r.data),
};
